import { useMutation, useQueryClient } from "@tanstack/react-query";
import { LogIn } from "lucide-react";
import { type FormEvent, useState } from "react";

import { api } from "../api/client";
import type { AuthSession, LicenceStatus } from "../api/types";
import { attentionSelector, designate } from "../components/attention";
import { Callout } from "../components/Callout";
import { Checkbox } from "../components/Checkbox";
import { Motion, Presence } from "../components/presence";
import { Button, Field, inputClass } from "../components/ui";
import { t } from "../i18n/i18n";
import { LicenceFrame } from "../licence/ActivationScreen";
import { formatLicenceDay, LICENCE_QUERY } from "../licence/licence";
import { readOnlyInfo } from "../licence/readOnly";
import { readOnlyText } from "../licence/ReadOnlyLicence";
import { licenceScope } from "../state/licenceScope";
import { SIGNED_OUT_KEY, sessionFlag, setSessionFlag } from "./auth";
import { AuthTitle, FormError, PasswordInput } from "./AuthForm";
import { authDate, authErrorText, seatNotMigrated } from "./authText";
import type { SignInScreen } from "./authState";
import { Waiting } from "../components/Waiting";

/** Envoi d'un écran de connexion : un refus « cookie du poste pas encore migré » (§4.4) fait relire l'état de la
 *  licence, qui le migre, puis l'envoi est rejoué une fois. */
export function useSignIn<T>(send: () => Promise<T>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      try {
        return await send();
      } catch (error) {
        if (!seatNotMigrated(error)) throw error;
        await queryClient.fetchQuery({ queryKey: LICENCE_QUERY, queryFn: api.licence, staleTime: 0 });
        return await send();
      }
    },
  });
}

/** Avis à toute l'unité affichés sur l'écran de connexion (§5.2, §5.8) : ouverture des comptes (7 jours), nominations
 *  d'administrateur (7 jours), lecture seule d'une licence échue (§3.12). */
export function UnitNotices({ session, licence }: { session: AuthSession; licence: LicenceStatus }) {
  const readOnly = readOnlyInfo(licence);
  const opened = session.opened;
  return (
    <>
      {readOnly && (
        <Callout tone="info" className="mt-3">
          {readOnlyText(readOnly.expiredAt, readOnly.until)}
        </Callout>
      )}
      {!readOnly && session.read_only_until && (
        <Callout tone="info" className="mt-3">
          {t("Lecture seule jusqu'au {date}", { date: formatLicenceDay(session.read_only_until) })}
        </Callout>
      )}
      {opened && (
        <Callout tone="info" className="mt-3">
          {t("Comptes nominatifs ouverts le {date} : administrateur de l'unité {nom}, compte créé depuis le poste « {poste} ». Si ce n'est pas la personne désignée par l'unité, prévenez BELLICOM.", {
            date: authDate(opened.at),
            nom: opened.admin || "—",
            poste: opened.seat || "—",
          })}
        </Callout>
      )}
      {(session.new_admins ?? []).map((named) => (
        <Callout key={`${named.at}-${named.name}`} tone="info" className="mt-3">
          {named.via === "request"
            ? t("Nouvel administrateur de l'unité : {nom}, nommé le {date} sur demande de {demandeur}, approuvée par {approbateur}. Si cette personne n'a pas été désignée par l'unité, prévenez un autre administrateur ou BELLICOM.", {
                nom: named.name || "—",
                date: authDate(named.at),
                demandeur: named.by || "—",
                approbateur: named.approver || "—",
              })
            : t("Nouvel administrateur de l'unité : {nom}, nommé le {date} par BELLICOM.", { nom: named.name || "—", date: authDate(named.at) })}
        </Callout>
      ))}
    </>
  );
}

/** Sous-titre des écrans de connexion : « {unité} · poste « {poste} » », ou « Poste « {poste} » ». */
export function seatSubtitle(session: AuthSession, licence: LicenceStatus): string {
  const seat = session.seat_name || licence.device_name || t("Poste sans nom");
  return session.unit_name
    ? t("{unite} · poste « {poste} »", { unite: session.unit_name, poste: seat })
    : t("Poste « {poste} »", { poste: seat });
}

/** Connexion (docs/17 §4.3, §8.3) : identifiant et mot de passe, sur un poste activé de la licence. */
export function LoginScreen({
  session,
  licence,
  onDone,
  onScreen,
}: {
  session: AuthSession;
  licence: LicenceStatus;
  onDone: (result: AuthSession) => void;
  onScreen: (screen: SignInScreen) => void;
}) {
  const space = licence.space_ref ?? null;
  const [remembered] = useState(() => licenceScope.rememberedLogin(space));
  const [login, setLogin] = useState(remembered ?? "");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(Boolean(remembered));
  const [forgot, setForgot] = useState(false);
  const [signedOut] = useState(() => {
    const flag = sessionFlag(SIGNED_OUT_KEY);
    if (flag) setSessionFlag(SIGNED_OUT_KEY, false);
    return flag;
  });
  const signIn = useSignIn(() => api.auth.login(login.trim(), password));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!login.trim()) return void designate(attentionSelector("auth-login"));
    if (!password) return void designate(attentionSelector("auth-password"));
    if (signIn.isPending) return;
    signIn.mutate(undefined, {
      onSuccess: (result) => {
        if (space) licenceScope.rememberLogin(space, remember ? login.trim() : null);
        setPassword("");
        onDone(result);
      },
      onError: () => setPassword(""),
    });
  };
  const missing = !login.trim() ? "auth-login" : !password ? "auth-password" : null;
  const admins = session.admins ?? [];

  return (
    <LicenceFrame>
      <AuthTitle icon={<LogIn size={16} />}>{t("Connexion")}</AuthTitle>
      <p className="mt-1 text-ui text-ink-soft">{seatSubtitle(session, licence)}</p>
      <Presence show={signedOut}>
        {signedOut && (
          <Callout tone="ok" className="mt-3">
            {t("Déconnecté")}
          </Callout>
        )}
      </Presence>
      <UnitNotices session={session} licence={licence} />
      <form onSubmit={submit} className="mt-4 space-y-3">
        <Field label={t("Identifiant")}>
          <input
            data-attention="auth-login"
            className={inputClass}
            value={login}
            maxLength={40}
            onChange={(e) => setLogin(e.target.value)}
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            autoFocus={!remembered}
          />
        </Field>
        <Field label={t("Mot de passe")}>
          <PasswordInput value={password} onChange={setPassword} current attention="auth-password" autoFocus={Boolean(remembered)} />
        </Field>
        <Checkbox checked={remember} onChange={setRemember} label={t("Retenir mon identifiant sur ce poste")} />
        <FormError message={signIn.error ? authErrorText(signIn.error, "login") : null} />
        <Button
          type="submit"
          className="w-full"
          disabled={signIn.isPending}
          blocked={Boolean(missing)}
          onBlocked={() => missing && designate(attentionSelector(missing))}
        >
          {signIn.isPending ? <Waiting>{t("Connexion…")}</Waiting> : t("Se connecter")}
        </Button>
      </form>
      <div className="mt-1 flex flex-col items-start">
        <Button variant="link" className="py-2.5" onClick={() => onScreen("redeem")}>
          {t("J'ai un code d'activation ou de réinitialisation")}
        </Button>
        <Button variant="link" className="py-2.5" onClick={() => setForgot((open) => !open)} aria-expanded={forgot}>
          {t("Mot de passe oublié ?")}
        </Button>
      </div>
      <Presence show={forgot}>
        {forgot && (
          <Motion origin="top" className="mt-2 rounded-lg border border-line px-3 py-2.5">
            <p className="text-ui leading-relaxed text-ink-soft">
              {t("Demandez un code de réinitialisation à un administrateur de l'unité : {noms}.", { noms: admins.length ? admins.join(", ") : "—" })}
            </p>
            <Button variant="link" className="mt-0.5 py-2.5" onClick={() => onScreen("recover")}>
              {t("Je suis administrateur et je n'ai plus accès")}
            </Button>
          </Motion>
        )}
      </Presence>
      <p className="mt-3 border-t border-line pt-3 text-meta leading-snug text-ink-muted">
        {t("Poste partagé : ne laissez pas le navigateur enregistrer votre mot de passe.")}
      </p>
    </LicenceFrame>
  );
}
